import { forwardRef, useEffect, useMemo } from "react";

import SlackHashLogo from "@/common/assets/slack-hash-logo.svg?react";
import AutocompleteSelect, {
  getStringFilterFunction,
  getStringRenderFunction,
} from "@/common/components/Autocomplete/AutocompleteSelect";
import { useEnvironment } from "@/common/contexts/EnvironmentContext";
import { useErrorToast } from "@/common/hooks/useErrorToast";
import {
  AutocompleteImplementationProps,
  AutocompleteValueIdProps,
} from "@/common/utils/autocomplete";
import { translateError } from "@/common/utils/errorHandling";
import { normalizeSlackChannel } from "@/common/utils/slack";
import useSlackChannels from "@/integration/hooks/useSlackChannels";
import { SlackChannel } from "@reflag/shared/slackConnectionAPI";

export type SlackChannelAutocompleteSelectProps =
  AutocompleteImplementationProps<SlackChannel> &
    AutocompleteValueIdProps<SlackChannel> & {
      showDefaultChannel?: boolean;
    };

export const SlackChannelAutocompleteSelect = forwardRef<
  HTMLButtonElement,
  SlackChannelAutocompleteSelectProps
>(function SlackChannelAutocompleteSelect(
  {
    valueAsId,
    value: valueProp,
    size,
    placeholder = "No channel selected",
    showDefaultChannel = true,
    canClear,
    isDisabled,
    onChange,
    ...autocompleteProps
  },
  ref,
) {
  const {
    data: channels = [],
    isLoading,
    isFetching,
    isError,
    error,
  } = useSlackChannels();
  const { org, app } = useEnvironment();
  const errorToast = useErrorToast();

  const defaultSlackChannel = normalizeSlackChannel(
    app.integrations?.slack?.channel ?? undefined,
  );

  const valueId = useMemo(
    () => (valueAsId ? valueProp : valueProp?.id),
    [valueAsId, valueProp],
  );

  const internalValue = useMemo(
    () => channels.find((c) => c.id === valueId),
    [channels, valueId],
  );

  const actualPlaceholder =
    (isError ? "Error loading channels" : undefined) ||
    (!isLoading && channels.length === 0 ? "No channels found" : undefined) ||
    (showDefaultChannel &&
      defaultSlackChannel?.name &&
      `${defaultSlackChannel.name} (default)`) ||
    placeholder;

  useEffect(() => {
    if (isError) {
      const { title, description } = translateError(
        error,
        `Failed to search for Slack channels`,
      );

      errorToast({
        title,
        description,
        duration: 5000,
      });
    }
  }, [isError, error, errorToast]);

  return (
    <AutocompleteSelect
      ref={ref}
      aria-label="Select a Slack channel"
      canClear={canClear}
      isDisabled={isDisabled || isError || !org.integrations.slack}
      isLoading={isLoading}
      isLoadingSuggestions={isFetching}
      itemFilterFn={getStringFilterFunction("name")}
      itemKeyProperty="id"
      itemRenderFn={getStringRenderFunction("name")}
      leftIcon={<SlackHashLogo height="14" />}
      loadingText="Loading..."
      maxSuggestions={50}
      placeholder={actualPlaceholder}
      size={size}
      suggestions={channels}
      value={internalValue}
      onChange={(newValue: SlackChannel | null) => {
        if (!newValue) {
          if (canClear) onChange?.(null);
          return;
        }
        if (valueAsId) {
          onChange?.(newValue?.id ?? null);
        } else {
          onChange?.(newValue ?? null);
        }
      }}
      onClear={() => {
        if (canClear) onChange?.(null);
      }}
      {...autocompleteProps}
    />
  );
});

SlackChannelAutocompleteSelect.displayName = "SlackChannelAutocompleteSelect";
